import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import InitialConfigForm from './InitialConfigForm';
import CalculationForm from './CalculationForm';
import EstimationHistoryGrid from './EstimationHistoryGrid';
import { ConfigValues, PartsVariables, LaborVariables } from './types';
import { EstimationRecord } from './utils/persistentDB';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';

const PurdyEstimationComponent: React.FC<{
  poNumber: string;
  claimUId: string;
  currentClaimForm?: any;
}> = ({ poNumber, claimUId, currentClaimForm }) => {
  const [configValues, setConfigValues] = useState<ConfigValues>({
    discount: 0,
    vat: 13,
    bodyworkRate: 0,
    paintRate: 0,
    mechanicalRate: 0,
    underinsurance: 0,
    depreciation: 0,
    deductible: 0,
    isStructuralLoss: false,
    isFinancialLoss: false,
  });
  const [partsVariables, setPartsVariables] = useState<PartsVariables>({
    workshop: 'Purdy Auto',
    company: 'Purdy Motor',
    brand: 'Toyota',
  });
  const [laborVariables, setLaborVariables] = useState<LaborVariables>({
    workshop: 'Purdy Auto',
    type: 'Interno',
  });
  const [jsonInput, setJsonInput] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [jsonSuccess, setJsonSuccess] = useState<string>('');
  const [initialCalculationValues, setInitialCalculationValues] = useState<any>(
    null,
  );
  const [formKey, setFormKey] = useState<number>(0);
  const [isJsonLoaded, setIsJsonLoaded] = useState<boolean>(false);
  const [historyKey, setHistoryKey] = useState(0); // Para forzar re-render del grid
  const [estimations, setEstimations] = useState<any[]>([]);

  // Función auxiliar para normalizar el JSON del API (convertir $oid y $date)
  const normalizeApiJson = (data: any): any => {
    if (!data || typeof data !== 'object') return data;

    // Si es un array, normalizar cada elemento
    if (Array.isArray(data)) {
      return data.map((item) => normalizeApiJson(item));
    }

    const normalized: any = {};

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];

        // Convertir $oid a string
        if (value && typeof value === 'object' && value.$oid) {
          normalized[key] = value.$oid;
        }
        // Convertir $date a string ISO
        else if (value && typeof value === 'object' && value.$date) {
          normalized[key] =
            typeof value.$date === 'string'
              ? value.$date
              : new Date(value.$date).toISOString();
        }
        // Recursivamente normalizar objetos anidados
        else if (value && typeof value === 'object' && !Array.isArray(value)) {
          normalized[key] = normalizeApiJson(value);
        }
        // Mantener arrays y valores primitivos
        else {
          normalized[key] = value;
        }
      }
    }

    return normalized;
  };

  const handleConfigUpdate = (values: any): void => {
    // Extract config values and conditional variables
    const {
      partsVariables: newPartsVariables,
      laborVariables: newLaborVariables,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      conditionalValues,
      ...configData
    } = values;

    // Siempre actualizar los valores de configuración, incluyendo los tipos de pérdida
    setConfigValues(configData);
    if (newPartsVariables) setPartsVariables(newPartsVariables);
    if (newLaborVariables) setLaborVariables(newLaborVariables);

    // Marcar que ya no es carga desde JSON para futuras actualizaciones
    if (isJsonLoaded) {
      setIsJsonLoaded(false);
    }
  };

  // Función para cargar una estimación desde el historial
  const handleLoadEstimation = (estimation: EstimationRecord): void => {
    console.log('📥 Cargando estimación desde historial:', estimation._id);

    try {
      // Normalizar el JSON del API (convertir $oid y $date)
      const normalizedEstimation = normalizeApiJson(
        estimation,
      ) as EstimationRecord;

      console.info('normalizedEstimation', normalizedEstimation);

      // Cargar valores de configuración directamente
      if (normalizedEstimation.configValues) {
        setConfigValues({
          discount: normalizedEstimation.configValues.discount || 0,
          vat: normalizedEstimation.configValues.vat || 13,
          bodyworkRate: normalizedEstimation.configValues.bodyworkRate || 0,
          paintRate: normalizedEstimation.configValues.paintRate || 0,
          mechanicalRate: normalizedEstimation.configValues.mechanicalRate || 0,
          underinsurance: normalizedEstimation.configValues.underinsurance || 0,
          depreciation: normalizedEstimation.configValues.depreciation || 0,
          deductible: normalizedEstimation.configValues.deductible || 0,
          isStructuralLoss:
            normalizedEstimation.configValues.isStructuralLoss || false,
          isFinancialLoss:
            normalizedEstimation.configValues.isFinancialLoss || false,
        });
      }

      // Cargar variables condicionales
      if (normalizedEstimation.conditionalVariables) {
        if (normalizedEstimation.conditionalVariables.partsVariables) {
          setPartsVariables({
            workshop:
              normalizedEstimation.conditionalVariables.partsVariables
                .workshop || 'Purdy Auto',
            company:
              normalizedEstimation.conditionalVariables.partsVariables
                .company || 'Purdy Motor',
            brand:
              normalizedEstimation.conditionalVariables.partsVariables.brand ||
              'Toyota',
          });
        }

        if (normalizedEstimation.conditionalVariables.laborVariables) {
          setLaborVariables({
            workshop:
              normalizedEstimation.conditionalVariables.laborVariables
                .workshop || 'Purdy Auto',
            type:
              normalizedEstimation.conditionalVariables.laborVariables.type ||
              'Interno',
          });
        }
      }

      // Cargar valores de cálculo
      if (normalizedEstimation.inputValues) {
        setInitialCalculationValues({
          netParts: normalizedEstimation.inputValues.netParts || 0,
          bodyworkHours: normalizedEstimation.inputValues.bodyworkHours || 0,
          paintHours: normalizedEstimation.inputValues.paintHours || 0,
          mechanicalHours:
            normalizedEstimation.inputValues.mechanicalHours || 0,
          hiddenDamagePartsNoVAT:
            normalizedEstimation.inputValues.hiddenDamagePartsNoVAT || 0,
          hiddenDamageLaborNoVAT:
            normalizedEstimation.inputValues.hiddenDamageLaborNoVAT || 0,
          additionalLabor:
            normalizedEstimation.inputValues.additionalLabor || 0,
          additionalParts:
            normalizedEstimation.inputValues.additionalParts || 0,
          subletAmount: normalizedEstimation.inputValues.subletAmount || 0,
          bodyworkUT: normalizedEstimation.inputValues.bodyworkUT || 0,
          paintUT: normalizedEstimation.inputValues.paintUT || 0,
          mechanicalUT: normalizedEstimation.inputValues.mechanicalUT || 0,
        });
      }

      // Guardar en localStorage que se cargó desde JSON para InitialConfigForm
      localStorage.setItem('estimation_json_loaded', 'true');
      localStorage.setItem(
        'estimation_json_values',
        JSON.stringify({
          bodyworkRate: normalizedEstimation.configValues?.bodyworkRate || 0,
          paintRate: normalizedEstimation.configValues?.paintRate || 0,
          mechanicalRate:
            normalizedEstimation.configValues?.mechanicalRate || 0,
          discount: normalizedEstimation.configValues?.discount || 0,
          partsVariables:
            normalizedEstimation.conditionalVariables?.partsVariables,
          laborVariables:
            normalizedEstimation.conditionalVariables?.laborVariables,
        }),
      );

      // Guardar balanceData en localStorage para CalculationForm
      if (
        normalizedEstimation.balanceData &&
        normalizedEstimation.balanceData.length > 0
      ) {
        localStorage.setItem(
          'estimation_balance_data',
          JSON.stringify(normalizedEstimation.balanceData),
        );
      }

      // Extract estimationId from normalizedEstimation if it exists (when coming from the API)
      if (normalizedEstimation.estimationId) {
        localStorage.setItem(
          'current_estimation_id',
          normalizedEstimation.estimationId,
        );
        console.log(
          '📝 EstimationId loaded from history:',
          normalizedEstimation.estimationId,
        );
      }

      // Forzar re-renderización de formularios
      setFormKey((prev) => prev + 1);
      setIsJsonLoaded(true);

      // Mostrar mensaje de éxito
      const timestamp = normalizedEstimation.timestamp || estimation.timestamp;
      setJsonSuccess(
        `Estimación cargada exitosamente desde ${new Date(
          timestamp,
        ).toLocaleString('es-CR')}`,
      );
      setJsonError('');

      console.log('✅ Estimación cargada exitosamente:', {
        configValues: normalizedEstimation.configValues,
        inputValues: normalizedEstimation.inputValues,
        conditionalVariables: normalizedEstimation.conditionalVariables,
        balanceData: normalizedEstimation.balanceData,
      });
    } catch (error) {
      console.error('❌ Error al cargar estimación:', error);
      setJsonError(
        `Error al cargar estimación: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`,
      );
      setJsonSuccess('');
    }
  };

  // Función para refrescar el historial
  const handleRefreshHistory = (): void => {
    loadEstimations();
    setHistoryKey((prev) => prev + 1);
  };

  // Función obsoleta - ya no se usa, se reemplazó por handleLoadEstimation directo
  // Se mantiene por si acaso hay referencias en otros lugares
  // @ts-expect-error - Función mantenida para uso futuro
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleLoadJson = (): void => {
    try {
      setJsonError('');
      setJsonSuccess('');

      const jsonData = JSON.parse(jsonInput);

      // Validar estructura del JSON
      if (!jsonData.configValues || !jsonData.inputValues) {
        throw new Error(
          'JSON inválido: debe contener configValues e inputValues',
        );
      }

      // Cargar configValues
      if (jsonData.configValues) {
        setConfigValues({
          discount: jsonData.configValues.discount || 0,
          vat: jsonData.configValues.vat || 13,
          bodyworkRate: jsonData.configValues.bodyworkRate || 0,
          paintRate: jsonData.configValues.paintRate || 0,
          mechanicalRate: jsonData.configValues.mechanicalRate || 0,
          underinsurance: jsonData.configValues.underinsurance || 0,
          depreciation: jsonData.configValues.depreciation || 0,
          deductible: jsonData.configValues.deductible || 0,
          isStructuralLoss: jsonData.configValues.isStructuralLoss || false,
          isFinancialLoss: jsonData.configValues.isFinancialLoss || false,
        });
      }

      // Cargar variables condicionales si existen
      if (jsonData.conditionalVariables) {
        if (jsonData.conditionalVariables.partsVariables) {
          setPartsVariables({
            workshop:
              jsonData.conditionalVariables.partsVariables.workshop ||
              'Purdy Auto',
            company:
              jsonData.conditionalVariables.partsVariables.company ||
              'Purdy Motor',
            brand:
              jsonData.conditionalVariables.partsVariables.brand || 'Toyota',
          });
        }

        if (jsonData.conditionalVariables.laborVariables) {
          setLaborVariables({
            workshop:
              jsonData.conditionalVariables.laborVariables.workshop ||
              'Purdy Auto',
            type:
              jsonData.conditionalVariables.laborVariables.type || 'Interno',
          });
        }
      }

      // Cargar valores de cálculo si existen
      if (jsonData.inputValues) {
        setInitialCalculationValues({
          netParts: jsonData.inputValues.netParts || 0,
          bodyworkHours: jsonData.inputValues.bodyworkHours || 0,
          paintHours: jsonData.inputValues.paintHours || 0,
          mechanicalHours: jsonData.inputValues.mechanicalHours || 0,
          hiddenDamagePartsNoVAT:
            jsonData.inputValues.hiddenDamagePartsNoVAT || 0,
          hiddenDamageLaborNoVAT:
            jsonData.inputValues.hiddenDamageLaborNoVAT || 0,
          additionalLabor: jsonData.inputValues.additionalLabor || 0,
          additionalParts: jsonData.inputValues.additionalParts || 0,
          subletAmount: jsonData.inputValues.subletAmount || 0,
          bodyworkUT: jsonData.inputValues.bodyworkUT || 0,
          paintUT: jsonData.inputValues.paintUT || 0,
          mechanicalUT: jsonData.inputValues.mechanicalUT || 0,
        });
      }

      // Guardar en localStorage que se cargó desde JSON
      localStorage.setItem('estimation_json_loaded', 'true');
      localStorage.setItem(
        'estimation_json_values',
        JSON.stringify({
          bodyworkRate: jsonData.configValues.bodyworkRate,
          paintRate: jsonData.configValues.paintRate,
          mechanicalRate: jsonData.configValues.mechanicalRate,
          discount: jsonData.configValues.discount,
          partsVariables: jsonData.conditionalVariables.partsVariables,
          laborVariables: jsonData.conditionalVariables.laborVariables,
        }),
      );

      setJsonSuccess(
        'Estimación cargada exitosamente! Los formularios se han actualizado.',
      );
      setJsonInput(''); // Limpiar el input
      setIsJsonLoaded(true); // Marcar que se cargó desde JSON
      setFormKey((prev) => prev + 1); // Forzar re-renderización de formularios
    } catch (error) {
      setJsonError(
        `Error al cargar JSON: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`,
      );
      setJsonSuccess('');
    }
  };

  const loadEstimations = useCallback(async () => {
    try {
      // Get custom estimations from API
      const dataEstimations = await new ClaimsTrackerClient().getCustomEstimations(
        claimUId,
      );
      const apiEstimations = dataEstimations?.result || [];

      // Eliminar duplicados basándose en el _id
      const uniqueEstimations = apiEstimations.filter(
        (estimation, index, self) =>
          index === self.findIndex((e) => e._id === estimation._id),
      );

      setEstimations(uniqueEstimations);
      console.log('🔄 Cargando estimaciones...', {
        api: apiEstimations.length,
        total: uniqueEstimations.length,
      });
    } catch (error) {
      console.error('❌ Error getting custom estimations:', error);
    }
  }, [claimUId]);

  useEffect(() => {
    loadEstimations();
  }, [loadEstimations]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="xl" sx={{ mt: 1, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Configure los parámetros y calcule las pérdidas de manera automática
          </Typography>

          {/* Grid de Historial de Estimaciones */}
          <Box sx={{ mb: 4 }}>
            <EstimationHistoryGrid
              key={historyKey}
              onLoadEstimation={handleLoadEstimation}
              onRefresh={handleRefreshHistory}
              loadedEstimations={estimations}
              claimUId={claimUId}
            />
          </Box>

          {jsonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {jsonError}
            </Alert>
          )}

          {jsonSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {jsonSuccess}
            </Alert>
          )}

          {/* Acordeón Parámetros Iniciales */}
          <Accordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  ▼
                </span>
              }
              aria-controls="initial-params-content"
              id="initial-params-header"
            >
              <Typography variant="h5" component="h2">
                Parámetros Iniciales
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <InitialConfigForm
                key={`config-${formKey}`}
                poNumber={poNumber}
                claimUId={claimUId}
                onNext={handleConfigUpdate}
                initialValues={configValues}
                isJsonLoaded={isJsonLoaded}
              />
            </AccordionDetails>
          </Accordion>

          {/* Acordeón Cálculo de Pérdidas */}
          <Accordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  ▼
                </span>
              }
              aria-controls="calculations-content"
              id="calculations-header"
            >
              <Typography variant="h5" component="h2">
                Cálculo de Pérdidas y Reparaciones
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CalculationForm
                key={`calculation-${formKey}`}
                poNumber={poNumber}
                claimUId={claimUId}
                configValues={configValues}
                partsVariables={partsVariables}
                laborVariables={laborVariables}
                initialCalculationValues={initialCalculationValues}
                siseSiniestro={currentClaimForm?.info?.siseSiniestro}
                onRefresh={handleRefreshHistory}
                estimations={estimations}
                isJsonLoaded={isJsonLoaded}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Container>
    </Box>
  );
};

export default PurdyEstimationComponent;
